import type { ClosingReadinessView, ClosingTaskView, FundingSummaryView } from '@tex/finance-shared'
import { type ReactNode, useState } from 'react'
import { errorMessage } from '../lib/api'
import { CLOSING_STATUS_LABELS, TASK_OWNER_LABELS, TASK_STATUS_LABELS, blockerText, useClosing, useFunding } from '../lib/closing'
import { useAccessDocument, useDocuments } from '../lib/documents'
import { formatAmount, formatDate, formatDay } from '../lib/format'
import { Tag } from './offers-card'
import { Alert, Card, Mono, Row } from './ui'

/**
 * The buyer's and the seller's view of closing: readiness in plain words, the task list (their own
 * with evidence, everyone else's by title/owner/status) and whether the money moved. The dealer
 * additionally sees the funding events, since the seller is the payee; never the offer terms.
 */
export function ClosingCard({ caseId, audience }: { caseId: string; audience: 'borrower' | 'dealer' }) {
  const closing = useClosing(caseId)
  if (closing.isPending) return <p className="text-sm text-neutral-500">Loading closing…</p>
  if (closing.isError) return <Alert tone="error">{errorMessage(closing.error)}</Alert>
  const view = closing.data
  if (view.audience !== audience) return <Alert tone="error">This view is only rendered from the {audience} projection.</Alert>

  return (
    <>
      <Card title="Closing">
        <ReadinessSummary readiness={view.readiness} funding={view.funding} />
        {view.audience === 'borrower' && view.selected_offer && (
          <p className="mt-2 text-sm text-neutral-600">
            Closing on the {view.selected_offer.partner_display_name} {view.selected_offer.terms.product_type} for{' '}
            <Mono>{formatAmount(view.selected_offer.terms.approved_amount)}</Mono>.
          </p>
        )}
        <TaskList caseId={caseId} tasks={view.tasks} mine={audience} />
        {audience === 'borrower' && (
          <p className="mt-3 text-xs text-neutral-400">
            Tasks marked “Buyer” are yours; upload the document under Documents and Tex will mark the task done once verified.
          </p>
        )}
      </Card>
      {audience === 'dealer' && <DealerFundingCard caseId={caseId} />}
    </>
  )
}

export function ReadinessSummary({
  readiness,
  funding,
}: {
  readiness: ClosingReadinessView
  funding: Pick<FundingSummaryView, 'partner_confirmed' | 'dealer_receipt_confirmed' | 'first_funded_on'>
}) {
  const tone = readiness.status === 'funded' ? 'green' : readiness.status === 'invalidated' ? 'red' : readiness.status === 'ready_to_close' || readiness.status === 'closing' ? 'blue' : 'grey'
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Tag tone={tone}>{CLOSING_STATUS_LABELS[readiness.status]}</Tag>
        {funding.partner_confirmed && <span className="text-neutral-600">Partner confirmed funding{funding.first_funded_on ? ` on ${formatDay(funding.first_funded_on)}` : ''}.</span>}
        {funding.dealer_receipt_confirmed && <span className="text-neutral-600">Seller confirmed receipt.</span>}
      </div>
      {readiness.reevaluation_required && <Alert tone="error">{readiness.reason ?? 'The selected offer no longer holds; closing cannot proceed.'}</Alert>}
      {readiness.blockers.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5 text-sm text-neutral-700">
          {readiness.blockers.map((b, i) => (
            <li key={i}>{blockerText(b)}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function TaskList<T extends ClosingTaskView>({ caseId, tasks, mine, renderActions }: { caseId: string; tasks: T[]; mine: 'borrower' | 'dealer' | 'tex'; renderActions?: (task: T) => ReactNode }) {
  if (tasks.length === 0) return <p className="mt-3 text-sm text-neutral-500">No closing tasks yet.</p>
  return (
    <ul className="mt-3 divide-y divide-neutral-100">
      {tasks.map((t) => (
        <li key={t.id} className="space-y-1 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-neutral-400">#{t.position}</span>
            <span className={`font-medium ${t.status === 'complete' || t.status === 'waived' ? 'text-neutral-500 line-through' : ''}`}>{t.title}</span>
            <Tag tone={t.status === 'complete' ? 'green' : t.status === 'blocked' ? 'red' : t.status === 'waived' ? 'grey' : 'blue'}>{TASK_STATUS_LABELS[t.status]}</Tag>
            <span className="text-xs text-neutral-500">
              {TASK_OWNER_LABELS[t.owner]}
              {t.owner === mine && mine !== 'tex' ? ' (you)' : ''}
              {t.due_on ? ` · due ${formatDay(t.due_on)}` : ''}
            </span>
          </div>
          {t.description && <p className="text-sm text-neutral-700">{t.description}</p>}
          {(t.evidence_document_id || t.completed_at || t.note) && (
            <div className="text-xs text-neutral-500">
              {t.completed_at && <span>{TASK_STATUS_LABELS[t.status]} {formatDate(t.completed_at)}. </span>}
              {t.evidence_document_id && <EvidenceLink caseId={caseId} documentId={t.evidence_document_id} />}
              {t.note && <span> {t.note}</span>}
            </div>
          )}
          {renderActions?.(t)}
        </li>
      ))}
    </ul>
  )
}

/** Names the evidence document and opens it through a short-lived URL, exactly like the Documents card. */
export function EvidenceLink({ caseId, documentId }: { caseId: string; documentId: string }) {
  const docs = useDocuments(caseId)
  const access = useAccessDocument(caseId)
  const [error, setError] = useState<string | null>(null)
  const doc = docs.data?.documents.find((d) => d.id === documentId) ?? null

  const open = async () => {
    setError(null)
    const tab = window.open('about:blank', '_blank')
    if (tab) tab.opener = null
    try {
      const { access: a } = await access.mutateAsync(documentId)
      if (tab) tab.location.href = a.url
      else window.location.assign(a.url)
    } catch (err) {
      tab?.close()
      setError(errorMessage(err))
    }
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      Evidence:{' '}
      <button type="button" className="text-tex-blue hover:underline disabled:opacity-50" onClick={open} disabled={access.isPending}>
        {doc ? doc.original_filename : <Mono>{documentId.slice(0, 8)}</Mono>}
      </button>
      {error && <span className="text-red-700">{error}</span>}
    </span>
  )
}

/** The seller sees what the partner confirmed it paid and what the seller confirmed it received — never which partner. */
function DealerFundingCard({ caseId }: { caseId: string }) {
  const funding = useFunding(caseId)
  if (funding.isPending) return null
  if (funding.isError) return <Alert tone="error">{errorMessage(funding.error)}</Alert>
  const f = funding.data
  return (
    <Card title="Funding">
      <dl>
        <Row label="Partner confirmed funded" value={<Mono>{formatAmount(f.summary.net_partner_funded)}</Mono>} />
        <Row label="You confirmed received" value={<Mono>{formatAmount(f.summary.net_dealer_received)}</Mono>} />
        <Row label="First funded" value={formatDay(f.summary.first_funded_on)} />
      </dl>
      {f.events.length > 0 && (
        <ul className="mt-3 divide-y divide-neutral-100 text-sm">
          {f.events.map((e) => (
            <li key={e.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
              <span>
                {e.kind === 'partner_confirmed_funding' ? 'Funding confirmed' : e.kind === 'dealer_confirmed_receipt' ? 'Receipt confirmed' : e.kind === 'reversal' ? 'Reversal' : 'Correction'}
                {e.funded_on ? ` · ${formatDay(e.funded_on)}` : ''}
                {e.external_reference ? ` · ref ${e.external_reference}` : ''}
              </span>
              <Mono>{formatAmount(e.amount)}</Mono>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-neutral-400">
        Tex records confirmations from the funding partner and from you; it does not hold or move money. Send your remittance confirmation to Tex to have receipt recorded.
      </p>
    </Card>
  )
}
